import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import EditButton from "@accredo/fe-shared/theme/components/EditButton.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { useAtom } from "jotai";
import { backendDataAtom } from "../../atoms/backendDataAtom.js";
import { useClaimCommunicationActions } from "../../hooks/useClaimCommunicationActions.jsx";
import ReminderForm from "./ReminderForm.jsx";
import ReminderDetailView from "./ReminderDetailView.jsx";

function ReminderModal({
  open,
  onClose,
  onSubmit = () => {},
  kommunikation_id,
  schaden_id,
  initialEditMode = false,
}) {
  const { user } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [editMode, setEditMode] = useState(initialEditMode);

  const { openCD, setOpenCD, tca, setTca, CDActions, setCDActions } =
    ContentDialogInit();

  const [backendData] = useAtom(backendDataAtom);
  const {
    createCommunication,
    updateCommunication,
    updateCommunicationReminder,
  } = useClaimCommunicationActions();

  const schaden = backendData?.data || {};

  const communicationTypeLabel = {
    note: "Notiz",
    tenant_message: "Mitteilung",
    insurer_email: "Mitteilung",
  };

  // Fetch kommunikation data when modal opens
  useEffect(() => {
    const fetchKommunikation = async () => {
      try {
        await backendGet(
          `data/kommunikations/${schaden_id}/${kommunikation_id}`,
          {
            options: {
              params: {
                // filter:  {id: kommunikation_id},
                include: ["createdBy", "reminder"],
              },
            },
            setResult: (res) => {
              const data = res || {};
              setReminder(data);
            },
            setIsLoading,
          }
        );
      } catch (error) {
        enqueueSnackbar("Fehler beim Laden der Kommunikation", {
          variant: "error",
        });
      }
    };

    if (open && kommunikation_id) {
      fetchKommunikation();
    } else {
      setReminder(null);
    }
    // eslint-disable-next-line
  }, [open, kommunikation_id]);

  useEffect(() => {
    if (open && kommunikation_id) {
      setOpenCD(true);
      setEditMode(true);
      setCDActions(null);
      setTca({
        title: "Erinnerung",
        maxWidth: "md",
        content: isLoading ? <div>Daten werden geladen...</div> : null,
        actions: null,
      });
    } else {
      setOpenCD(false);
      setCDActions(null);
    }
    // eslint-disable-next-line
  }, [open, kommunikation_id, isLoading]);

  useEffect(() => {
    if (open && reminder) {
      processReminderToView(reminder);
    }
    // eslint-disable-next-line
  }, [open, reminder, editMode]);

  const processReminderToView = (data) => {
    const isOwner = data?.created_by?.id === user.id;
    const isAccredo =
      user.role_names.includes("sysadmin") ||
      user.role_names.includes("accredo");
    const canEdit = isOwner || isAccredo;

    const defaultValues = {
      ...data,
      schaden_id,
      faellig_am: data?.reminder?.faellig_am ?? null,
      ist_erledigt: !!data?.reminder?.ist_erledigt,
    };

    setCDActions({
      top:
        canEdit && !editMode ? (
          <EditButton
            onClick={() => {
              setEditMode(true);
              setCDActions({ top: null });
            }}
          />
        ) : null,
    });

    setTca({
      title: `Erinnerung ${reminder?.type ? "für " + communicationTypeLabel[reminder.type] : ""}`,
      maxWidth: "md",
      content: editMode ? (
        <ReminderForm
          key={`${kommunikation_id}-edit`}
          defaultValues={defaultValues}
          onSubmit={async (formData) => {
            try {
              await updateCommunicationReminder(
                kommunikation_id,
                { ...formData },
                {
                  onSuccess: () => {
                    enqueueSnackbar("Erinnerung gespeichert", {
                      variant: "success",
                    });
                    onSubmit();
                    onClose();
                  },
                }
              );
              return true;
            } catch (e) {
              return false;
            }
          }}
          onCancel={onClose}
          showIstErledigt={true}
          disabled={false}
        />
      ) : (
        <ReminderDetailView
          key={`${kommunikation_id}-detail`}
          reminder={data}
          onClose={onClose}
        />
      ),
      actions: null,
    });
  };

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      CDActions={CDActions}
      onClose={onClose}
    />
  );
}

ReminderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  kommunikation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  schaden_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ReminderModal;
